import React, { Suspense } from 'react';
import { useLocation, useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PrivateRoute from './PrivateRoute';
import Root from './Root';
import * as layoutConstants from '../constants/layout';

// All layouts/containers
import DefaultLayout from '../layouts/Default';
import VerticalLayout from '../layouts/Vertical';
import DetachedLayout from '../layouts/Detached';
import HorizontalLayout from '../layouts/Horizontal';
import FullLayout from '../layouts/Full';

import MetadataUpdater from '../components/MetadataUpdater';

// lazy load all the views

// auth
const Login = React.lazy(() => import('../pages/account/Login'));
const Logout = React.lazy(() => import('../pages/account/Logout'));
const Register = React.lazy(() => import('../pages/account/Register'));
const Confirm = React.lazy(() => import('../pages/account/Confirm'));
const ForgetPassword = React.lazy(() => import('../pages/account/ForgetPassword'));
const LockScreen = React.lazy(() => import('../pages/account/LockScreen'));

const LoginKeycloak = React.lazy(() => import('../pages/account/LoginKeycloak'));
const TestApiKeycloak = React.lazy(() => import('../pages/account/TestApiKeycloak'));


// dashboard
const ProjectDashboard = React.lazy(() => import('../pages/dashboard/Project'));

// hospital
const HospitalList = React.lazy(() => import('../pages/hospital/HospitalList'));
const HospitalAdd = React.lazy(() => import('../pages/hospital/AddHospital'));
const HospitalDashboard = React.lazy(() => import('../pages/hospital/HospitalDashboard'));
const InvoiceDashboard = React.lazy(() => import('../pages/invoice/Invoice'));

// bank account
const BankAccountList = React.lazy(() => import('../pages/bankAccount/BankAccountList'));

// issue type
const IssueTypeList = React.lazy(() => import('../pages/issueType/IssueTypeList'));

// team
const TeamList = React.lazy(() => import('../pages/team/TeamList'));
const TeamAdd = React.lazy(() => import('../pages/team/AddTeam'));
const TeamEdit = React.lazy(() => import('../pages/team/EditTeam'));
const UserTableList = React.lazy(() => import('../pages/team/UserTable'));

// user
const UserList = React.lazy(() => import('../pages/user/UserList'));
const UserAdd = React.lazy(() => import('../pages/user/AddUser'));
const UserEdit = React.lazy(() => import('../pages/user/EditUser'));
const ChangePassword = React.lazy(() => import('../pages/user/ChangePassword'));
const UserPrivillege = React.lazy(() => import('../pages/user/UserPrivillege'));

// product
const ProductList = React.lazy(() => import('../pages/product/ProductList'));
const ProductAdd = React.lazy(() => import('../pages/product/AddProduct'));
const AddVersion = React.lazy(() => import('../pages/product/AddVersion'));
const DetailVersion = React.lazy(() => import('../pages/product/DetailVersion'));
const ProductEdit = React.lazy(() => import('../pages/product/EditProduct'));

// requestFeature
const RequestFeatureList = React.lazy(() => import('../pages/requestFeature/RequestFeatureList'));
const RequestFeatureAdd = React.lazy(() => import('../pages/requestFeature/AddRequestFeature'));
const RequestFeatureEdit = React.lazy(() => import('../pages/requestFeature/EditRequestFeature'));
const DetailRequestFeature = React.lazy(() => import('../pages/requestFeature/DetailRequestFeature'));

// updateFeature
const UpdateFeatureList = React.lazy(() => import('../pages/updateFeature/UpdateFeatureList'));
const DetailUpdate = React.lazy(() => import('../pages/updateFeature/DetailUpdate'));

//Job - Recap
const JobRecap = React.lazy(() => import('../pages/jobRecap/JobRecap'));

//updateService
const UpdateServiceList = React.lazy(() => import('../pages/service/UpdateServiceList'));
const DetailService = React.lazy(() => import('../pages/service/DetailService'));

// Permintaan Pelayanan DetailRequestService
const RequestServiceList = React.lazy(() => import('../pages/service/RequestServiceList'));
const RequestServiceAdd = React.lazy(() => import('../pages/service/AddRequestService'));
const DetailRequestService = React.lazy(() => import('../pages/service/DetailRequestService'));
const RequestServiceProgressHistory = React.lazy(() => import('../pages/service/RequestServiceProgressHistory'));


//Report
const Report = React.lazy(() => import('../pages/report/Report'));
const TeamStatistic = React.lazy(() => import('../pages/report/TeamStatistic'));

//InvoiceList
const InvoiceList = React.lazy(() => import('../pages/invoice/InvoiceList'));
const InvoiceReport = React.lazy(() => import('../pages/invoice/InvoiceReport'));

// error
const ErrorPageNotFound = React.lazy(() => import('../pages/error/PageNotFound'));
const ErrorPageNotFoundAlt = React.lazy(() => import('../pages/error/PageNotFoundAlt'));
const ServerError = React.lazy(() => import('../pages/error/ServerError'));
const UnauthorizedAccess = React.lazy(() => import('../pages/error/UnauthorizedAccessPage'));
const AccessDenied = React.lazy(() => import('../pages/error/AccessDeniedPage'));

// - other
const Invoice = React.lazy(() => import('../pages/other/Invoice'));
const FAQ = React.lazy(() => import('../pages/other/FAQ'));
const Pricing = React.lazy(() => import('../pages/other/Pricing'));
const Maintenance = React.lazy(() => import('../pages/other/Maintenance'));
const Starter = React.lazy(() => import('../pages/other/Starter'));
const PreLoader = React.lazy(() => import('../pages/other/PreLoader'));
const Timeline = React.lazy(() => import('../pages/other/Timeline'));
const PaymentSuccess = React.lazy(() => import('../pages/other/PaymentSuccess'));
const PaymentFailed = React.lazy(() => import('../pages/other/PaymentFailed'));

// Sprint
const Sprint = React.lazy(() => import('../pages/sprint/SprintPage'));
const SprintDetail = React.lazy(() => import('../pages/sprint/SprintPageDetail'));
const SprintHistory = React.lazy(() => import('../pages/sprint/SprintHistoryPage'));
const SprintReport = React.lazy(() => import('../pages/sprint/SprintReportPage'));

// Night Shift
const NightShiftPage = React.lazy(() => import('../pages/nightShift/NightShiftPage'));

// Team Shift Assignment
const TeamShiftAssignment = React.lazy(() => import('../pages/teamShiftAssignment/teamShiftAssignmentPage'));

const loading = () => <div className=""></div>;

// type LoadComponentProps = {
//     component: React.LazyExoticComponent<() => JSX.Element>,
// };

const LoadComponent = ({ component: Component }) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);

const nameDescription = "Evoluz by Farmagitech";
const routeMetadata = {
    '/account/login': { title: `Login - ${nameDescription}`, description: `${nameDescription} - Login to Evoluz` },
    'account/register': { title: `Register - ${nameDescription}`, description: `Register to Evoluz` },
    'account/confirm': { title: `Confirmation - ${nameDescription}`, description: `Confirmation of Evoluz` },
    'account/lock-screen': { title: `Lock Screen`, description: `Lock Screen of Evoluz` },
    'account/logout': { title: `Logout - ${nameDescription}`, description: `Logout from Evoluz` },
    'error-404': { title: `404 - Page Not Found`, description: `Page is not found` },
    'error-500': { title: `500 - Internal Server Error`, description: `Internal server error` },
    'maintenance': { title: `Maintenance`, description: `This page is under maintenance` },
    'unauthorized-access': { title: `401 - Unauthorized Access`, description: `You don't have authorization access` },
    'payment-success': { title: 'Pembayaran Berhasil', description: 'Halaman konfirmasi pembayaran berhasil' },
    'payment-failed': { title: 'Pembayaran Gagal', description: 'Halaman konfirmasi pembayaran gagal' },
    '/dashboard/project': { title: `Dashboard`, description: `Evoluz - Dashboard project page` },
    '/apps/invoice': { title: `Invoice`, description: `Evoluz - Invoice page` },
    '/apps/invoice/detail': { title: `Invoice Detail`, description: `Evoluz - Invoice detail page` },
    '/apps/hospital': { title: `Rumah Sakit`, description: `Evoluz - Hospital page` },
    '/apps/hospital/add': { title: `Tambah Rumah Sakit`, description: `Evoluz - Add hospital page` },
    '/apps/hospital/edit': { title: `Edit Hospital`, description: `Evoluz - Edit hospital page` },
    '/apps/hospital/dashboard': { title: ``, description: `Evoluz - Dashboard hospital page` },
    'apps/issue-type': { title: 'Tipe Kendala Aplikasi', description: 'Evoluz - Tipe Kendala Aplikas' },
    '/apps/dashboard/invoice': { title: `Invoice Dashboard Hospital`, description: `Evoluz - Invoice dashboard hospital page` },
    '/apps/team': { title: `Tim`, description: `Evoluz - Team page` },
    '/apps/team/add': { title: `Tambah Tim`, description: `Evoluz - Add team page` },
    '/apps/team/edit': { title: `Ubah Data`, description: `Evoluz - Edit team page` },
    '/apps/team/detail': { title: `Lihat Data`, description: `Evoluz - Detail team page` },
    '/apps/user': { title: `Pengguna`, description: `Evoluz - User page` },
    '/apps/user/add': { title: `Tambah Pengguna`, description: `Evoluz - Add user page` },
    '/apps/user/edit': { title: ``, description: `Evoluz - Edit user page` },
    '/apps/privillege': { title: `Privillege user`, description: `Evoluz - Privillege user page` },
    '/apps/changePassword': { title: `Change password`, description: `Evoluz - Change password page` },
    '/apps/product': { title: `Produk`, description: `Evoluz - Product page` },
    '/apps/product/add': { title: `Tambah product`, description: `Evoluz - Add product page` },
    '/apps/product/edit/addVersion': { title: `Edit Add Version Product`, description: `Evoluz - Edit Add version product page` },
    '/apps/product/edit/detailVersion': { title: `Edit Detail Version Product`, description: `Evoluz - Edit detail version product page` },
    '/apps/product/edit': { title: `Ubah Data Produk`, description: `Evoluz - Edit product page` },
    '/apps/request-feature': { title: `Usulan Perubahan / Fitur`, description: `Evoluz - Request feature page` },
    '/apps/request-feature/add': { title: `Add Request Feature`, description: `Evoluz - Add request feature page` },
    '/apps/request-feature/edit': { title: `Edit Request Feature`, description: `Evoluz - Edit request feature page` },
    '/apps/request-feature/detail': { title: `Detail Request Feature`, description: `Evoluz - Detail request feature page` },
    '/apps/update-feature': { title: `Daftar Pembaruan Fitur`, description: `Evoluz - Update feature page` },
    '/apps/update-feature/detailUpdate': { title: `Detail Update Feature`, description: `Evoluz - Detail update feature page` },
    '/apps/request-service': { title: `Permintaan Pelayanan`, description: `Evoluz - Request service page` },
    '/apps/request-service/add': { title: `Add Request Service`, description: `Evoluz - Add request service page` },
    '/apps/request-service/detail': { title: `Detail Request Service`, description: `Evoluz - Detail request service page` },
    '/apps/request-service/progress-history': { title: 'Riwayat Permintaan Pelayanan', description: 'Evoluz - Request Service History Progress Page' },
    '/apps/update-service': { title: `Tindak Lanjut Pelayanan`, description: `Evoluz - Update service page` },
    '/apps/update-service/detailService': { title: `Detail Update Service`, description: `Evoluz -  page` },
    '/apps/report': { title: `Laporan / Monitor Pekerjaan`, description: `Evoluz - Report page` },
    '/apps/job_recap': { title: `Laporan / Rekap Pekerjaan`, description: `Evoluz - Job recap page` },
    '/apps/report_statistic': { title: `Laporan / Rekap Statistik Tim`, description: `Evoluz - Report statistic page` },
    '/apps/invoice': { title: `Invoice`, description: `Evoluz - Invoice page` },
    '/apps/invoice/invoice_item': { title: `Invoice Item`, description: `Evoluz - Invoice item page` },
    '/pages/stater': { title: `Starter`, description: `Evoluz - Starter page` },
    '/pages/pricing': { title: `Pricing`, description: `Evoluz - Pricing page` },
    '/pages/error-404-alt': { title: `Error 404 Alt`, description: `Evoluz - Error 404 alt page` },
    '/pages/timeline': { title: `Timeline`, description: `Evoluz - Timeline page` },
    '/pages/invoice': { title: `Invoice`, description: `Evoluz - Invoice page` },
    '/pages/faq': { title: `Faq`, description: `Evoluz - Faq page` },
    '/pages/preloader': { title: `Preloader`, description: `Evoluz - Preloader page` },
    '/apps/sprint': { title: 'Sprint Workflow', description: 'Evoluz - Sprint Workflow Page' },
    '/apps/sprint/task-detail': { title: 'Sprint Workflow Task Detail', description: 'Evoluz - Sprint Workflow Task Detail Page' },
    '/apps/sprint/history': { title: 'Sprint History', description: 'Evoluz - Sprint History' },
    '/apps/sprint/report/:id': { title: 'Laporan Sprint', description: 'Evoluz - Laporan Sprint: Menghitung task, persentase progress, task per time' },
    '/apps/night-shift': { title: 'Jam Kerja Shift Malam', description: 'Evoluz - Night SHift Working Hour' },
    '/apps/team-shift-assignment': { title: 'Penjadwalan Jam Kerja', description: 'Evoluz - Pengelolaan Jadwal Kerja Tim, seperti export import jadwal kerja juga termasuk penjadwalan jadwal kerja' },
    '/': { title: ``, description: `` },
};


const AllRoutes = () => {
    const location = useLocation();
    const { layout } = useSelector((state) => ({
        layout: state.Layout,
    }));

    const getLayout = () => {
        let layoutCls = VerticalLayout;

        switch (layout.layoutType) {
            case layoutConstants.LAYOUT_HORIZONTAL:
                layoutCls = HorizontalLayout;
                break;
            case layoutConstants.LAYOUT_DETACHED:
                layoutCls = DetachedLayout;
                break;
            case layoutConstants.LAYOUT_FULL:
                layoutCls = FullLayout;
                break;
            default:
                layoutCls = VerticalLayout;
                break;
        }
        return layoutCls;
    };
    let Layout = getLayout();

    return (
        <>
            <MetadataUpdater routeMetadata={routeMetadata} />
            {
                useRoutes(
                    [
                        { path: '/', element: <Root /> },
                        { path: '/login-keycloak', element: <LoginKeycloak /> },
                        { path: '/test-api-keycloak', element: <TestApiKeycloak /> },
                        {
                            // public routes
                            path: '/',
                            element: <DefaultLayout />,
                            children: [
                                {
                                    path: 'account',
                                    children: [
                                        { path: 'login', element: <LoadComponent component={ErrorPageNotFoundAlt} /> },
                                        { path: 'register', element: <LoadComponent component={Register} /> },
                                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                                        // { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                                        { path: 'login-keycloak', element: <LoadComponent component={LoginKeycloak} /> },
                                    ],
                                },
                                {
                                    path: 'error-404',
                                    element: <LoadComponent component={ErrorPageNotFound} />,
                                },
                                {
                                    path: 'error-500',
                                    element: <LoadComponent component={ServerError} />,
                                },
                                {
                                    path: 'maintenance',
                                    element: <LoadComponent component={Maintenance} />,
                                },
                                {
                                    path: 'unauthorized-access',
                                    element: <LoadComponent component={UnauthorizedAccess} />,
                                },
                                {
                                    path: 'access-denied',
                                    element: <LoadComponent component={AccessDenied} />,
                                },
                                {
                                    path: 'payment-success',
                                    element: <LoadComponent component={PaymentSuccess} />,
                                },
                                {
                                    path: 'payment-failed',
                                    element: <LoadComponent component={PaymentFailed} />,
                                },
                            ],
                        },
                        {
                            // auth protected routes
                            /**
                             * User Type List for Access
                             * {
                             *      1. admin
                             *      2. team
                             *      3. client
                             *      4. client_premium
                             * }
                             */
                            path: '/',
                            element: <PrivateRoute component={Layout} whoCanAccess={['super_admin', 'admin', 'team', 'client', 'client_premium']} />,
                            children: [
                                {
                                    path: 'dashboard',
                                    children: [
                                        {
                                            path: 'project',
                                            element: <LoadComponent component={ProjectDashboard} />,
                                        },
                                    ],
                                },
                                {
                                    path: '/apps',
                                    children: [
                                        {
                                            path: 'invoice',
                                            element: <PrivateRoute component={HospitalDashboard} whoCanAccess={['client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'invoice/detail',
                                            element: <PrivateRoute component={InvoiceDashboard} whoCanAccess={['client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'hospital',
                                            element: <PrivateRoute component={HospitalList} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'hospital/add',
                                            element: <PrivateRoute component={HospitalAdd} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'hospital/dashboard',
                                            element: <PrivateRoute component={HospitalDashboard} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'hospital/dashboard/invoice',
                                            element: <PrivateRoute component={InvoiceDashboard} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'issue-type',
                                            element: <PrivateRoute component={IssueTypeList} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'bank-account',
                                            element: <PrivateRoute component={BankAccountList} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'team',
                                            element: <PrivateRoute component={TeamList} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'team/add',
                                            element: <PrivateRoute component={TeamAdd} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'team/edit',
                                            element: <PrivateRoute component={TeamEdit} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'team/detail',
                                            element: <PrivateRoute component={UserTableList} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'user',
                                            element: <PrivateRoute component={UserList} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'user/add',
                                            element: <PrivateRoute component={UserAdd} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'user/edit',
                                            element: <PrivateRoute component={UserEdit} whoCanAccess={['super_admin', 'admin', 'client', 'team']} />,
                                        },
                                        {
                                            path: 'user/privillege',
                                            element: <PrivateRoute component={UserPrivillege} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'user/changePassword',
                                            element: <PrivateRoute component={ChangePassword} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium', 'team']} />,
                                        },
                                        {
                                            path: 'product',
                                            element: <PrivateRoute component={ProductList} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium', 'team']} />,
                                        },
                                        {
                                            path: 'product/add',
                                            element: <PrivateRoute component={ProductAdd} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'product/edit/addVersion',
                                            element: <PrivateRoute component={AddVersion} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'product/edit/detailVersion',
                                            element: <PrivateRoute component={DetailVersion} whoCanAccess={['super_admin', 'admin', 'client_premium', 'client', 'team']} />,
                                        },
                                        {
                                            path: 'product/edit',
                                            element: <PrivateRoute component={ProductEdit} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium', 'team']} />,
                                        },
                                        {
                                            path: 'request-feature',
                                            element: <PrivateRoute component={RequestFeatureList} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'request-feature/add',
                                            element: <PrivateRoute component={RequestFeatureAdd} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'request-feature/edit',
                                            element: <PrivateRoute component={RequestFeatureEdit} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'request-feature/detail',
                                            element: <PrivateRoute component={DetailRequestFeature} whoCanAccess={['super_admin', 'admin', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'update-feature',
                                            element: <PrivateRoute component={UpdateFeatureList} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'update-feature/detailUpdate',
                                            element: <PrivateRoute component={DetailUpdate} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'request-service',
                                            element: <PrivateRoute component={RequestServiceList} whoCanAccess={['super_admin', 'admin', 'team', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'request-service/add',
                                            element: <PrivateRoute component={RequestServiceAdd} whoCanAccess={['super_admin', 'admin', 'team', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'request-service/detail',
                                            element: <PrivateRoute component={DetailRequestService} whoCanAccess={['super_admin', 'admin', 'team', 'client', 'client_premium']} />,
                                        },
                                        {
                                            path: 'request-service/progress-history',
                                            element: <PrivateRoute component={RequestServiceProgressHistory} whoCanAccess={['super_admin', 'admin', 'team', 'client', 'client_premium']} />
                                        },
                                        {
                                            path: 'update-service',
                                            element: <PrivateRoute component={UpdateServiceList} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'update-service/detailService',
                                            element: <PrivateRoute component={DetailService} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'report',
                                            element: <PrivateRoute component={Report} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'job_recap',
                                            element: <PrivateRoute component={JobRecap} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'report_statistic',
                                            element: <PrivateRoute component={TeamStatistic} whoCanAccess={['super_admin', 'admin']} />,
                                        },
                                        {
                                            path: 'report-invoice',
                                            element: <PrivateRoute component={InvoiceReport} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'report-invoice/invoice',
                                            element: <PrivateRoute component={InvoiceDashboard} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        // {
                                        //     path: 'invoice',
                                        //     element: <PrivateRoute component={InvoiceList} whoCanAccess={['super_admin', 'admin']} />,
                                        // },
                                        // {
                                        //     path: 'invoice/invoice_item',
                                        //     element: <PrivateRoute component={InvoiceItem} whoCanAccess={['super_admin', 'admin']} />,
                                        // },
                                        {
                                            path: 'sprint',
                                            element: <PrivateRoute component={Sprint} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'sprint/task-detail',
                                            element: <PrivateRoute component={SprintDetail} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                        {
                                            path: 'sprint/history',
                                            element: <PrivateRoute component={SprintHistory} whoCanAccess={['super_admin', 'admin', 'team']} />
                                        },
                                        {
                                            path: 'sprint/report/:id',
                                            element: <PrivateRoute component={SprintReport} whoCanAccess={['super_admin', 'admin', 'team']} />
                                        },
                                        {
                                            path: 'night-shift',
                                            element: <PrivateRoute component={NightShiftPage} whoCanAccess={['super_admin', 'admin', 'team']} />
                                        },
                                        {
                                            path: 'team-shift-assignment',
                                            element: <PrivateRoute component={TeamShiftAssignment} whoCanAccess={['super_admin', 'admin', 'team']} />,
                                        },
                                    ],
                                },
                                {
                                    path: 'pages',
                                    children: [
                                        {
                                            path: 'starter',
                                            element: <LoadComponent component={Starter} />,
                                        },
                                        {
                                            path: 'pricing',
                                            element: <LoadComponent component={Pricing} />,
                                        },
                                        {
                                            path: 'error-404-alt',
                                            element: <LoadComponent component={ErrorPageNotFoundAlt} />,
                                        },
                                        {
                                            path: 'timeline',
                                            element: <LoadComponent component={Timeline} />,
                                        },
                                        {
                                            path: 'invoice',
                                            element: <LoadComponent component={Invoice} />,
                                        },
                                        {
                                            path: 'faq',
                                            element: <LoadComponent component={FAQ} />,
                                        },
                                        {
                                            path: 'preloader',
                                            element: <LoadComponent component={PreLoader} />,
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                    location,
                    { key: location.pathname }
                )
            }
        </>
    );
};

export { AllRoutes };
